(ns frontend.view.components.kpi
  (:require-macros [frontend.macros :refer [without-default-action-and-propagation]])
  (:require
   [common.currencies :as currencies]
   [common.localization :as loc]
   [common.util :as cu :refer [flip]]
   [ui.components.sortable-list.view :as sortable-list]
   [frontend.utils :as fu]
   [frontend.view.components.form.view :as form]
   [potpuri.core :as p :refer [fn-> fn->>]]
   [ui.components.button.view :as button]
   [ui.editors.data-input :as data-input]))

(defn kpi-attribute
  [entity-type kw]
  (when (and (keyword? entity-type)
             (keyword? kw))
    (keyword (name entity-type) (name kw))))

(defn get-attr
  [attribute-keyword kpi-entity]
  (when kpi-entity
    (let [entity-type (:entity/type kpi-entity)
          attr (kpi-attribute entity-type attribute-keyword)]
      (get kpi-entity attr))))

(def get-user-guidances (partial get-attr :user-guidances))
(def get-kpis (partial get-attr :kpis))
(def get-questions (partial get-attr :questions))
(def get-ordering (partial get-attr :ordering))
(def get-currency (partial get-attr :currency))

(defn user-guidance-element
  [{:keys [kw user-guidance kpi-entity
           update-guidance! save-guidance!
           end-guidance-edit!]
    :as   view-args}]
  (let [placeholder (if (cu/separator-kw? kw)
                      (loc/tr :group-heading-placeholder)
                      (loc/tr :edit-guidance))]
    [form/view
     {:wrapper          (fu/cs "zap-form__light-grey__green__dark-grey kpi-user-guidance"
                               (when (cu/separator-kw? kw)
                                 "kpi-group-heading"))
      :placeholder      placeholder
      :type             "text"
      :edit?            true
      :empty-on-submit? true
      :value            user-guidance
      :on-change!       (partial update-guidance! kpi-entity kw)
      :on-save!         (partial save-guidance! kpi-entity kw)
      :cancel-edit!     (partial end-guidance-edit! kpi-entity kw)
      :control-id       (str "kpi-user-guidance" kw)}]))

(defn to-js
  [m]
  (->> m
       (map (fn [[k v]]
              [(if (keyword? k)
                 (subs (str k) 1)
                 k)
               (if (keyword? v)
                 (subs (str v) 1)
                 v)]))
       (into {})))

(defn my-clj->js
  [x]
  (cond
    (string? x) x
    (keyword? x) (subs (str x) 1)
    (map? x) (reduce (fn [m [k v]]
                       (assoc m (my-clj->js k) (my-clj->js v)))
                     {}
                     x)
    (coll? x) (apply array (map my-clj->js x))
    :else x))

(def keywordize-kpi-vals
  #{:kw :type})

(defn kpi-to-clj
  [kpi]
  (let [kpi (js->clj kpi :keywordize-keys true)
        kpi (->> kpi
                 (map (fn [[k v]]
                        (if (contains? keywordize-kpi-vals k)
                          [k (keyword v)]
                          [k v])))
                 (into {}))]
    kpi))

(def keywordize-vals
  #{:kpi-category/kpis :kpi-category/questions
    :kpi-category/granularity :entity/type
    :kpi-category/currency
    :kpi-report/currency :kpi-report/kpis :kpi-report/questions
    ;;TODO other report attributes?
    })

(defn kpi-entity-to-clj
  [kpi-entity]
  (let [kpi-entity (js->clj kpi-entity :keywordize-keys true)
        kpi-entity (->> kpi-entity
                        (map (fn [[k v]]
                               (cond
                                 (contains? keywordize-vals k)
                                 [k
                                  (if (coll? v)
                                    (set (map keyword v))
                                    (keyword v))]
                                 (contains? #{:kpi-category/ordering
                                              ;;TODO other entities?
                                              :kpi-report/ordering}
                                            k)
                                 [k (p/map-vals (partial map keyword) v)]
                                 :else
                                 [k v])))
                        (into {}))]
    kpi-entity))

(defn render-kpi-item
  [{:keys [removegroupheading! togglekpi! saveguidance! updateguidance! endguidanceedit! kpi kpi-entity] :as props}]
  (let [heading? (->> kpi
                      :kw
                      cu/separator-kw?)]
    [:div
     {:class-name (fu/cs "kpi-field"
                         (when heading?
                           "kpi-group-heading-field"))}
     [:i
      {:class "fas fa-grip-vertical ml-2"
       :title (loc/tr :drag-to-reorder)}]

     [:div.kpi-item-details
      (if heading?
        [:span.group-heading.d-none (loc/tr :group-heading-title)]
        [:span (fu/kpi-str kpi)])
      [:div
       [user-guidance-element
        {:kw                 (:kw kpi)
         :user-guidance      (->> kpi
                                  :kw
                                  (get (get-user-guidances kpi-entity)))
         :save-guidance!     saveguidance!
         :update-guidance!   updateguidance!
         :end-guidance-edit! endguidanceedit!
         :kpi-entity         kpi-entity}]]
      [button/secondary-open
       {:tooltip (loc/tr :kpi/remove-kpi-tooltip)}
       [:span.remove-kpi
        {:on-click (fn [_]
                     (if heading?
                       (removegroupheading! (:type kpi) kpi-entity (:kw kpi))
                       (togglekpi! kpi-entity (:kw kpi) false)))}
        [:i.fal.fa-times]]]]]))

(defn kpis-view
  [{:keys [state alerts kpi-entity kpis currency
           currency-attribute show-manage-button?
           toggle-kpi! reorder! save-guidance! save-kpi-entity!
           update-guidance! end-guidance-edit!
           open-kpi-curtain! remove-group-heading! add-group-heading!]}]
  (let [bs-alert (:kpis alerts)
        kpi-entity-kpis (set kpis)
        kpis (fu/all-kpis)
        separator-kws (->> kpi-entity-kpis
                           (filter cu/separator-kw?))
        separator-kpis (->> separator-kws
                            (map (fn [kw]
                                   {:kw kw})))
        selected-kpis (->> (concat kpis separator-kpis)
                           (filter (fn->> :kw kpi-entity-kpis)))
        kpi-entity-ordering (-> kpi-entity
                                get-ordering
                                :quantitative)
        temp-ordering (->> @state
                           :ordering
                           :quantitative)
        ordered-kpis (->> (or temp-ordering kpi-entity-ordering)
                          (filter (fn [kpi]
                                    (or (contains? kpi-entity-kpis kpi)
                                        (cu/separator-kw? kpi)))))
        unordered-kpis (->> selected-kpis
                            (map :kw)
                            (remove (set ordered-kpis)))
        selected-kpi-kws (vec (concat ordered-kpis unordered-kpis))
        kw-ords (->> selected-kpi-kws
                     (map-indexed (fn [a b] [b a]))
                     (into {}))
        selected-kpis (->> selected-kpi-kws
                           (map (fn [kw]
                                  {:kw kw :type :quantitative :visible? true})))
        selected-kpis (->> selected-kpis
                           (sort-by (comp kw-ords :kw)))
        ords (comp kw-ords :kw)]
    [:div.kpi-wizard-section
     [:div.kpi-wizard-section-title
      (loc/tr :kpi-wizard/quantitative-title)]
     [:div.px-4
      [:div.kpi-settings-currency
       [:div.kpi-settings-currency-label (loc/tr :currency)]
       [:div.kpi-settings-currency-field
        [data-input/view
         {:editor-type   :editor.type/select
          :data-type     :data.type/keyword
          :is-clearable? false
          :full-width?   true
          :value         currency
          :options       currencies/value-options
          :on-change     (fn [new-value & _]
                           (save-kpi-entity!
                            kpi-entity
                            currency-attribute
                            new-value
                            (fn [_])
                            (fn [_])))}]]]

      [:div
       [:div
        [:div.kpi-fields
         [sortable-list/view
          {:items selected-kpis
           :get-key :kw
           :on-reorder (fn [{:keys [source-index destination-index]}]
                         (reorder! :quantitative kpi-entity selected-kpi-kws source-index destination-index))
           :render-item (fn [{:keys [item]}]
                          [render-kpi-item
                           {:kpi item
                            :kpi-entity          kpi-entity
                            :removegroupheading! remove-group-heading!
                            :togglekpi!          toggle-kpi!
                            :saveguidance!       save-guidance!
                            :updateguidance!     update-guidance!
                            :endguidanceedit!    end-guidance-edit!}])}]]
        (when show-manage-button?
          [:div
           [button/primary-open
            {:on-click (without-default-action-and-propagation
                        (open-kpi-curtain! :quantitative kpi-entity))}
            (loc/tr :kpi-manage)]])
        [:div
         [button/primary-open
          {:on-click (without-default-action-and-propagation
                      (add-group-heading! :quantitative kpi-entity (vec selected-kpi-kws)))}
          [:i.fal.fa-plus]
          [:span (loc/tr :add-heading)]]]]]]]))

(defn questions-view
  [{:keys [state alerts kpi-entity questions show-manage-button?
           toggle-kpi! reorder! save-guidance!
           update-guidance! end-guidance-edit!
           open-kpi-curtain! remove-group-heading! add-group-heading!]}]
  (let [alert (:questions alerts)
        kpi-entity-questions (set questions)
        questions (fu/all-kpi-questions)
        separator-kws (->> kpi-entity-questions
                           (filter cu/separator-kw?))
        separator-questions (->> separator-kws
                                 (map (fn [kw]
                                        {:kw kw})))
        selected-questions (->> (concat questions separator-questions)
                                (filter (fn->> :kw kpi-entity-questions)))
        kpi-entity-ordering (-> kpi-entity
                                get-ordering
                                :qualitative)
        temp-ordering (->> @state
                           :ordering
                           :qualitative)
        ordered-questions (->> (or temp-ordering kpi-entity-ordering)
                               (filter (fn [q]
                                         (or (contains? kpi-entity-questions q)
                                             (cu/separator-kw? q)))))
        unordered-questions (->> selected-questions
                                 (map :kw)
                                 (remove (set ordered-questions)))
        selected-question-kws (vec (concat ordered-questions unordered-questions))
        kw-ords (->> selected-question-kws
                     (map-indexed (fn [a b] [b a]))
                     (into {}))
        selected-questions (->> selected-question-kws
                                (map (fn [kw]
                                       {:kw kw :type :qualitative :visible? true})))
        selected-questions (->> selected-questions
                                (sort-by (comp kw-ords :kw)))
        ords (comp kw-ords :kw)]
    [:div.kpi-wizard-section
     [:div.kpi-wizard-section-title
      (loc/tr :kpi-wizard/qualitative-title)]
     [:div
      {:class "px-4"}
      [:div
       [:div
        [:div.kpi-fields
         [sortable-list/view
          {:items selected-questions
           :get-key :kw
           :on-reorder (fn [{:keys [source-index destination-index]}]
                         (reorder! :qualitative kpi-entity selected-question-kws source-index destination-index))
           :render-item (fn [{:keys [item]}]
                          [render-kpi-item
                           {:kpi item
                            :kpi-entity          kpi-entity
                            :removegroupheading! remove-group-heading!
                            :togglekpi!          toggle-kpi!
                            :saveguidance!       save-guidance!
                            :updateguidance!     update-guidance!
                            :endguidanceedit!    end-guidance-edit!}])}]]
        (when show-manage-button?
          [:div
           [button/primary-open
            {:on-click (without-default-action-and-propagation
                        (open-kpi-curtain! :qualitative kpi-entity))}
            (loc/tr :kpi-manage)]])
        [:div
         [button/primary-open
          {:on-click (without-default-action-and-propagation
                      (add-group-heading! :qualitative kpi-entity (vec selected-question-kws)))}
          [:i.fal.fa-plus]
          [:span (loc/tr :add-heading)]]]]]]]))

(defn kpi-view
  [view-args]
  (fn
    [{:keys [kpi-entity state reorder! save-kpi-entity! toggle-kpi! open-kpi-curtain!
             add-group-heading! remove-group-heading! save-guidance! update-guidance!
             end-guidance-edit!]
      :as   view-args}]
    (let [{:keys [edit-kpi-info? alerts]} @state
          alert (:kpi-entity alerts)
          kpi-entity-id (:db/id kpi-entity)
          edit-kpi-entity (-> @state
                              :edit-kpi-entity
                              (get kpi-entity-id))
          edit? (-> @state
                    :edit-kpi-entity?
                    (get kpi-entity-id))
          kpi-entity-values (merge kpi-entity edit-kpi-entity)
          entity-type (:entity/type kpi-entity)
          currency (get-currency kpi-entity-values)
          kpis (get-kpis kpi-entity-values)
          questions (get-questions kpi-entity-values)
          currency-attribute (kpi-attribute entity-type :currency)]
      [:div.kpi-wizard-section-wrapper.px-2.px-md-3

       [kpis-view
        {:kpi-entity            kpi-entity
         :save-kpi-entity!      save-kpi-entity!
         :currency              currency
         :currency-attribute    currency-attribute
         :state                 state
         :kpis                  kpis
         :alerts                alerts
         :reorder!              reorder!
         :open-kpi-curtain!     open-kpi-curtain!
         :add-group-heading!    add-group-heading!
         :remove-group-heading! remove-group-heading!
         :save-guidance!        save-guidance!
         :update-guidance!      update-guidance!
         :end-guidance-edit!    end-guidance-edit!
         :toggle-kpi!           toggle-kpi!}]

       [questions-view
        {:kpi-entity            kpi-entity
         :state                 state
         :questions             questions
         :alerts                alerts
         :reorder!              reorder!
         :open-kpi-curtain!     open-kpi-curtain!
         :add-group-heading!    add-group-heading!
         :remove-group-heading! remove-group-heading!
         :save-guidance!        save-guidance!
         :update-guidance!      update-guidance!
         :end-guidance-edit!    end-guidance-edit!
         :toggle-kpi!           toggle-kpi!}]])))
